import type {
  SurveyTree,
  SurveyValidationIssue,
  SurveyValidationResult,
} from './types';

/**
 * Caso 08 — Local tree validator. Hosts call `validateTree` before saving so
 * the same rules light up in the UI and on the wire. The backend's
 * `POST /xy-survey/:id/validate` will mirror this logic (so server stays
 * authoritative) but the package version makes the editor responsive.
 *
 * v0 ships the obvious-mistakes pass; later iterations add semantic checks
 * (e.g. wheel-link without a target prize pool, demographic specialField
 * mismatched with the actual question kind).
 */
export function validateTree(tree: SurveyTree): SurveyValidationResult {
  const issues: SurveyValidationIssue[] = [];

  if (!tree.name?.trim()) {
    issues.push({
      path: 'name',
      message: 'A survey needs a name.',
      severity: 'error',
    });
  }

  if (!tree.defaultLanguage) {
    issues.push({
      path: 'defaultLanguage',
      message: 'A survey needs a default language.',
      severity: 'error',
    });
  }

  if (tree.languages && tree.defaultLanguage &&
      !tree.languages.includes(tree.defaultLanguage)) {
    issues.push({
      path: 'defaultLanguage',
      message: 'Default language must be part of `languages`.',
      severity: 'error',
    });
  }

  if (!tree.steps?.length) {
    issues.push({
      path: 'steps',
      message: 'Add at least one step.',
      severity: 'error',
    });
  }

  tree.steps?.forEach((step, sIdx) => {
    if (!step.questions?.length) {
      issues.push({
        path: `steps[${sIdx}].questions`,
        message: 'Each step needs at least one question.',
        severity: 'warning',
      });
    }
    step.questions?.forEach((q, qIdx) => {
      const base = `steps[${sIdx}].questions[${qIdx}]`;
      if (!q.label?.trim()) {
        issues.push({
          path: `${base}.label`,
          message: 'Question is missing a label.',
          severity: 'error',
        });
      }
      if ((q.kind === 'singleChoice' || q.kind === 'multiChoice') &&
          (!q.options || q.options.length < 2)) {
        issues.push({
          path: `${base}.options`,
          message: 'Choice questions need at least two options.',
          severity: 'error',
        });
      }
      q.options?.forEach((opt, oIdx) => {
        if (!opt.optionKey?.trim()) {
          issues.push({
            path: `${base}.options[${oIdx}].optionKey`,
            message: 'Every option needs a stable optionKey for translations.',
            severity: 'error',
          });
        }
      });
    });
  });

  return {
    ok: issues.every((i) => i.severity !== 'error'),
    issues,
  };
}
